import { LISTING_ISSUES } from "@/lib/listingIssues";
import { LISTING_PHOTO_SLOT_SLUG } from "@/lib/listingPhotoFeedback";
import { getPresetEvidenceRules } from "@/lib/listing-review-rounds/presetEvidence";
import type {
  ListingReviewSnapshot,
  PresetEvidenceRule,
} from "@/lib/listing-review-rounds/types";

export type UnmetResubmitPreset = {
  slug: string;
  kind: PresetEvidenceRule["kind"];
  message: string;
  photo_slot?: 1 | 2 | 3;
};

export type ResubmitEligibilityResult =
  | { allowed: true }
  | {
      allowed: false;
      code: "RESUBMIT_BLOCKED";
      unmet_presets: UnmetResubmitPreset[];
    };

const PHOTO_SLOT_MESSAGES: Record<1 | 2 | 3, string> = {
  1: "Replace your main photo before resubmitting.",
  2: "Replace gallery photo 2 before resubmitting.",
  3: "Replace gallery photo 3 before resubmitting.",
};

const TEXT_FIELD_MESSAGES: Record<"title" | "description" | "defects", string> =
  {
    title: "Update your title to address the feedback before resubmitting.",
    description:
      "Update your description to address the feedback before resubmitting.",
    defects:
      "Update defects or condition notes to address the feedback before resubmitting.",
  };

function normalizeTextForEvidenceCompare(value: string): string {
  return value.trim().replace(/\s+/g, " ");
}

function storagePathForSlot(
  snapshot: ListingReviewSnapshot,
  slot: 1 | 2 | 3,
): string | null {
  const row = snapshot.images.find((image) => image.slot === slot);
  return row?.storage_path ?? null;
}

/** True when the flagged slot has a different storage path than at round open. */
export function photoSlotEvidenceMet(
  baseline: ListingReviewSnapshot,
  current: ListingReviewSnapshot,
  slot: 1 | 2 | 3,
): boolean {
  const beforePath = storagePathForSlot(baseline, slot);
  const afterPath = storagePathForSlot(current, slot);
  if (afterPath === beforePath) {
    return false;
  }
  return Boolean(afterPath);
}

export function textFieldEvidenceMet(
  baseline: ListingReviewSnapshot,
  current: ListingReviewSnapshot,
  field: "title" | "description" | "defects",
): boolean {
  return (
    normalizeTextForEvidenceCompare(baseline[field]) !==
    normalizeTextForEvidenceCompare(current[field])
  );
}

function issueLabelForSlug(slug: string): string | null {
  return LISTING_ISSUES.find((issue) => issue.id === slug)?.label ?? null;
}

function unmetMessageForRule(rule: PresetEvidenceRule): string {
  if (rule.kind === "photo_slot" && rule.photoSlot) {
    if (rule.slug === "image_quality") {
      return PHOTO_SLOT_MESSAGES[rule.photoSlot];
    }
    const label = issueLabelForSlug(rule.slug);
    if (label) {
      return `${label} — replace the photo before resubmitting.`;
    }
    return PHOTO_SLOT_MESSAGES[rule.photoSlot];
  }
  if (
    rule.kind === "title" ||
    rule.kind === "description" ||
    rule.kind === "defects"
  ) {
    return TEXT_FIELD_MESSAGES[rule.kind];
  }
  const label = issueLabelForSlug(rule.slug);
  return label
    ? `Address “${label}” before resubmitting.`
    : "Address the requested change before resubmitting.";
}

function ruleEvidenceMet(
  rule: PresetEvidenceRule,
  baseline: ListingReviewSnapshot,
  current: ListingReviewSnapshot,
): boolean {
  if (rule.kind === "photo_slot" && rule.photoSlot) {
    return photoSlotEvidenceMet(baseline, current, rule.photoSlot);
  }
  if (
    rule.kind === "title" ||
    rule.kind === "description" ||
    rule.kind === "defects"
  ) {
    return textFieldEvidenceMet(baseline, current, rule.kind);
  }
  // Price and unknown kinds are not enforced on resubmit in Phase 3.
  return true;
}

/** Preset slugs admins may mark satisfied (non-price evidence only). */
export function presetSlugsEligibleForMarkSatisfied(
  reasonCodes: string[],
): string[] {
  const enforceable = new Set<string>();
  for (const rule of getPresetEvidenceRules(reasonCodes)) {
    if (rule.kind !== "price") {
      enforceable.add(rule.slug);
    }
  }
  return reasonCodes.filter((slug) => enforceable.has(slug));
}

/**
 * Compare current listing state to the open admin round snapshot.
 * Skips price presets (Phase 4+) and satisfied presets (admin override).
 */
export function evaluateResubmitEligibility(input: {
  reasonCodes: string[];
  satisfiedPresets?: string[];
  baselineSnapshot: ListingReviewSnapshot | null;
  currentSnapshot: ListingReviewSnapshot;
}): ResubmitEligibilityResult {
  if (!input.baselineSnapshot) {
    return { allowed: true };
  }

  const satisfied = new Set(input.satisfiedPresets ?? []);
  const rules = getPresetEvidenceRules(input.reasonCodes).filter(
    (rule) => rule.kind !== "price" && !satisfied.has(rule.slug),
  );

  const unmet: UnmetResubmitPreset[] = [];
  const seen = new Set<string>();

  for (const rule of rules) {
    if (ruleEvidenceMet(rule, input.baselineSnapshot, input.currentSnapshot)) {
      continue;
    }
    const key =
      rule.kind === "photo_slot" && rule.photoSlot
        ? `${rule.slug}:${rule.photoSlot}`
        : rule.slug;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    unmet.push({
      slug: rule.slug,
      kind: rule.kind,
      message: unmetMessageForRule(rule),
      ...(rule.photoSlot ? { photo_slot: rule.photoSlot } : {}),
    });
  }

  if (unmet.length === 0) {
    return { allowed: true };
  }

  return {
    allowed: false,
    code: "RESUBMIT_BLOCKED",
    unmet_presets: unmet,
  };
}

export function photoSlotSlugForIndex(slot: 1 | 2 | 3): string {
  return LISTING_PHOTO_SLOT_SLUG[slot];
}
